<template>
  <TitleLayout>
    <div class="space-y-6 fade-in">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
            Closing Documents
          </h1>
          <p class="text-gray-600 mt-1">Upload your HUD-1 and generate closing documents.</p>
        </div>
        <router-link to="/dashboard" class="btn btn-secondary">
          <font-awesome-icon icon="arrow-left" class="mr-2" />
          Back
        </router-link>
      </div>

      <!-- Tolerance Check -->
      <ToleranceBanner :tolerance-status="toleranceStatus" />

      <!-- Progress Summary -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold text-gray-900">Pre-Generation Checklist</h3>
        </div>
        <div class="card-body">
          <div class="space-y-3">
            <div class="flex items-center">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                :class="closingProgress.titleInfoComplete ? 'bg-success-100' : 'bg-gray-100'"
              >
                <font-awesome-icon
                  :icon="closingProgress.titleInfoComplete ? 'check' : 'times'"
                  :class="closingProgress.titleInfoComplete ? 'text-success-600' : 'text-gray-400'"
                  class="text-xs"
                />
              </div>
              <span :class="closingProgress.titleInfoComplete ? 'text-gray-900' : 'text-gray-500'">
                Title information updated
              </span>
            </div>
            <div class="flex items-center">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                :class="closingProgress.feesComplete ? 'bg-success-100' : 'bg-gray-100'"
              >
                <font-awesome-icon
                  :icon="closingProgress.feesComplete ? 'check' : 'times'"
                  :class="closingProgress.feesComplete ? 'text-success-600' : 'text-gray-400'"
                  class="text-xs"
                />
              </div>
              <span :class="closingProgress.feesComplete ? 'text-gray-900' : 'text-gray-500'">
                Closing fees entered
              </span>
            </div>
            <div class="flex items-center">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                :class="closingProgress.payoffsComplete ? 'bg-success-100' : 'bg-gray-100'"
              >
                <font-awesome-icon
                  :icon="closingProgress.payoffsComplete ? 'check' : 'times'"
                  :class="closingProgress.payoffsComplete ? 'text-success-600' : 'text-gray-400'"
                  class="text-xs"
                />
              </div>
              <span :class="closingProgress.payoffsComplete ? 'text-gray-900' : 'text-gray-500'">
                Payoffs reviewed
              </span>
            </div>
            <div class="flex items-center">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                :class="closingProgress.previewGenerated ? 'bg-success-100' : 'bg-gray-100'"
              >
                <font-awesome-icon
                  :icon="closingProgress.previewGenerated ? 'check' : 'times'"
                  :class="closingProgress.previewGenerated ? 'text-success-600' : 'text-gray-400'"
                  class="text-xs"
                />
              </div>
              <span :class="closingProgress.previewGenerated ? 'text-gray-900' : 'text-gray-500'">
                Documents previewed
              </span>
            </div>
            <div class="flex items-center">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                :class="!hasToleranceViolation ? 'bg-success-100' : 'bg-danger-100'"
              >
                <font-awesome-icon
                  :icon="!hasToleranceViolation ? 'check' : 'times'"
                  :class="!hasToleranceViolation ? 'text-success-600' : 'text-danger-600'"
                  class="text-xs"
                />
              </div>
              <span :class="!hasToleranceViolation ? 'text-gray-900' : 'text-danger-600'">
                Fees within tolerance
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Generate Documents -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold text-gray-900">Generate Closing Documents</h3>
        </div>
        <div class="card-body">
          <p class="text-gray-600 mb-6">
            When you're ready, click the button below to generate the final closing documents.
            The loan officer will be notified when documents are ready for review.
          </p>

          <button
            @click="generateDocuments"
            class="btn btn-success w-full py-4 text-lg"
            :disabled="!canGenerate || isGenerating"
          >
            <font-awesome-icon v-if="isGenerating" icon="spinner" class="mr-2 animate-spin" />
            <font-awesome-icon v-else icon="file-contract" class="mr-2" />
            {{ isGenerating ? 'Generating Documents...' : 'Generate Closing Documents' }}
          </button>

          <p v-if="!canGenerate" class="text-sm text-gray-500 mt-3 text-center">
            Complete all checklist items above before generating documents.
          </p>

          <div v-if="generateError" class="mt-4 tolerance-exceeded rounded-lg p-3">
            <span class="text-sm text-danger-800">{{ generateError }}</span>
          </div>

          <div v-if="generateSuccess" class="mt-4 tolerance-ok rounded-lg p-4">
            <div class="flex items-center">
              <font-awesome-icon icon="check-circle" class="text-success-600 mr-3 text-xl" />
              <div>
                <p class="font-semibold text-success-800">Documents Generated Successfully!</p>
                <p class="text-sm text-success-700 mt-1">
                  The loan officer has been notified and will review the documents.
                </p>
              </div>
            </div>
            <a
              v-if="documentUrl"
              :href="documentUrl"
              target="_blank"
              class="btn btn-primary mt-4"
            >
              <font-awesome-icon icon="download" class="mr-2" />
              Download Documents
            </a>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <div class="flex items-center justify-between">
        <router-link to="/preview" class="btn btn-secondary">
          <font-awesome-icon icon="arrow-left" class="mr-2" />
          Preview
        </router-link>
        <router-link to="/dashboard" class="btn btn-secondary">
          Back to Dashboard
          <font-awesome-icon icon="home" class="ml-2" />
        </router-link>
      </div>
    </div>
  </TitleLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import TitleLayout from '@/components/Layout/TitleLayout.vue'
import ToleranceBanner from '@/components/ToleranceBanner/index.vue'
import apiService from '@/api/apiService'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Documents',
  components: {
    TitleLayout,
    ToleranceBanner,
  },
  setup() {
    const store = useStore()

    const isGenerating = ref(false)
    const generateError = ref(null)
    const generateSuccess = ref(false)
    const documentUrl = ref(null)

    const toleranceStatus = computed(() => store.getters.toleranceStatus)
    const hasToleranceViolation = computed(() => store.getters.hasToleranceViolation)
    const closingProgress = computed(() => store.getters.closingProgress)

    const canGenerate = computed(() => {
      return (
        closingProgress.value.titleInfoComplete &&
        closingProgress.value.feesComplete &&
        closingProgress.value.payoffsComplete &&
        closingProgress.value.previewGenerated &&
        !hasToleranceViolation.value
      )
    })

    const generateDocuments = async () => {
      if (!canGenerate.value) return

      isGenerating.value = true
      generateError.value = null
      generateSuccess.value = false

      try {
        const data = await apiService.generateDocuments({
          document_types: ['hud', 'security_instruments', 'closing_instructions'],
        })

        // Backend returns documents array with url fields (S3 presigned URLs)
        if (data.documents && data.documents.length > 0) {
          // Use first document URL for backward compatibility
          documentUrl.value = data.documents[0].url || data.documents[0].file_url
        } else if (data.file_url) {
          // Fallback for older response format
          documentUrl.value = data.file_url
        }

        generateSuccess.value = true
        store.dispatch('setClosingProgress', { documentsGenerated: true })

      } catch (err) {
        console.error('[Documents] Generation failed:', err)
        
        // Handle error response with incomplete sections
        const errorData = err.response?.data
        if (errorData) {
          if (errorData.incomplete_sections && Array.isArray(errorData.incomplete_sections)) {
            const sectionsList = errorData.incomplete_sections.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')
            generateError.value = `${errorData.detail || 'Cannot generate documents'}. Missing: ${sectionsList}`
          } else if (errorData.status) {
            // Update local progress from API status
            const status = errorData.status
            store.dispatch('setClosingProgress', {
              titleInfoComplete: status.title_info_complete || false,
              feesComplete: status.fees_complete || false,
              payoffsComplete: status.payoffs_complete || false,
            })
            generateError.value = errorData.detail || 'Cannot generate documents. Please complete all required sections.'
          } else {
            generateError.value = errorData.detail || 'Failed to generate documents. Please try again.'
          }
        } else {
          generateError.value = 'Failed to generate documents. Please try again.'
        }
        
        // Refresh closing status after error to sync with backend
        await fetchClosingStatus()
      } finally {
        isGenerating.value = false
      }
    }

    const fetchClosingStatus = async () => {
      try {
        const data = await apiService.getClosingStatus()
        
        // Update local store with API status
        if (data.progress) {
          store.dispatch('setClosingProgress', {
            titleInfoComplete: data.progress.title_info_complete || false,
            feesComplete: data.progress.fees_complete || false,
            payoffsComplete: data.progress.payoffs_complete || false,
            previewGenerated: data.progress.preview_generated || false,
            documentsGenerated: data.progress.documents_generated || false,
          })
        }
        
        // Update tolerance status if included
        if (data.tolerance_status) {
          store.dispatch('setToleranceStatus', data.tolerance_status)
        }
      } catch (err) {
        console.error('[Documents] Failed to fetch closing status:', err)
      }
    }

    const fetchToleranceStatus = async () => {
      try {
        const data = await apiService.getToleranceStatus()
        store.dispatch('setToleranceStatus', data)
      } catch (err) {
        console.error('[Documents] Failed to fetch tolerance status:', err)
      }
    }

    onMounted(() => {
      fetchClosingStatus()
      fetchToleranceStatus()
    })

    return {
      isGenerating,
      generateError,
      generateSuccess,
      documentUrl,
      toleranceStatus,
      hasToleranceViolation,
      closingProgress,
      canGenerate,
      generateDocuments,
      fetchClosingStatus,
    }
  },
}
</script>

